import { supabase } from "./supabaseClient";

export const authService = {
  // Função para registrar um novo usuário (convidado por um admin)
  signUp: async (email, password, userMetadata = {}) => {
    // Verificar se é um domínio de teste (example.com, test.com, etc)
    const isTestDomain = email.includes('@example.com') ||
                         email.includes('@test.com') ||
                         email.includes('@localhost') ||
                         email.includes('@dev.') ||
                         email.includes('@staging.');

    // Se for domínio de teste, usar a rota de bypass
    if (isTestDomain) {
      try {
        const response = await fetch('/api/auth/register-test', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            password,
            full_name: userMetadata.full_name || "",
            role: userMetadata.role || "user",
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Erro ao criar usuário');
        }

        const data = await response.json();
        return {
          user: {
            id: data.user.id,
            email: data.user.email,
            user_metadata: {
              full_name: data.user.full_name,
              role: data.user.role,
            }
          },
          session: null
        };
      } catch (error) {
        throw new Error(error.message || 'Erro ao criar usuário com domínio de teste');
      }
    }

    // Para emails normais, usar a autenticação nativa do Supabase
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: userMetadata.full_name || "",
          role: userMetadata.role || "user",
        },
      },
    });

    if (error) {
      throw new Error(error.message);
    }
    return data;
  },

  // Função para fazer login
  login: async (email, password, rememberMe = false) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: {
        shouldCreateUser: false, // Não cria usuário se não existir
        data: {
          // Você pode adicionar metadados aqui se necessário
        },
        // Define a persistência da sessão com base no rememberMe
        persistSession: rememberMe ? true : false,
      },
    });

    if (error) {
      throw new Error(error.message);
    }
    return data;
  },

  // Função para fazer logout
  logout: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw new Error(error.message);
    }
    // Limpar dados de login local
    localStorage.removeItem("rememberedEmail");
  },

  // Função para enviar email de recuperação de senha
  sendPasswordResetEmail: async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/update-password`, // Redireciona para uma página de atualização de senha
    });

    if (error) {
      throw new Error(error.message);
    }
    return true;
  },

  // Atualiza a senha do usuário autenticado pela sessão de recuperação
  // (aberta a partir do link de redirectTo de sendPasswordResetEmail).
  updatePassword: async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });

    if (error) {
      throw new Error(error.message);
    }
    return true;
  },

  // Verifica se o usuário está autenticado
  isAuthenticated: async () => {
    const { data } = await supabase.auth.getSession();
    return data.session !== null;
  },

  // Obtém o usuário atual da sessão
  getCurrentUser: async () => {
    const { data } = await supabase.auth.getUser();
    return data.user;
  },

  // Obtém a role do usuário atual.
  // app_metadata é a fonte real de autorização (só o service-role escreve;
  // user_metadata é auto-editável pelo próprio utilizador via Auth API — #109).
  // O fallback para user_metadata é só transitório, para não esconder botões
  // de admins/supervisores já migrados cuja sessão ainda não tem app_metadata
  // fresco; o servidor nunca confia nesse fallback.
  getCurrentUserRole: async () => {
    const user = await authService.getCurrentUser();
    if (user) {
      return user.app_metadata?.role || user.user_metadata?.role || "user";
    }
    return null;
  },

  // Buscar usuário por ID usando a função existente no banco
  getUserById: async (userId) => {
    try {
      // Primeiro, tentar buscar através da função RPC existente
      const { data: fullName, error: rpcError } = await supabase.rpc('get_user_full_name_by_id', {
        p_user_id: userId
      });

      if (!rpcError && fullName) {
        return {
          id: userId,
          email: null, // A função não retorna email
          full_name: fullName,
          display_name: fullName
        };
      }

      // Como alternativa, se o usuário logado tem acesso, usar auth.getUser() para comparar
      const currentUser = await authService.getCurrentUser();
      
      if (currentUser && currentUser.id === userId) {
        return {
          id: currentUser.id,
          email: currentUser.email,
          full_name: currentUser.user_metadata?.full_name || currentUser.user_metadata?.display_name || currentUser.email,
          display_name: currentUser.user_metadata?.full_name || currentUser.user_metadata?.display_name || currentUser.email
        };
      }

      // Se não conseguir buscar, retornar dados parciais
      console.warn(`Não foi possível buscar dados completos do usuário ${userId}`);
      return {
        id: userId,
        email: null,
        full_name: `Usuário ${userId.substring(0, 8)}...`,
        display_name: `Usuário ${userId.substring(0, 8)}...`
      };
      
    } catch (err) {
      console.warn(`Erro ao buscar usuário ${userId}:`, err.message);
      return {
        id: userId,
        email: null,
        full_name: `Usuário ${userId.substring(0, 8)}...`,
        display_name: `Usuário ${userId.substring(0, 8)}...`
      };
    }
  },

  // Listener para mudanças no estado de autenticação
  onAuthStateChange: (callback) => {
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        callback(event, session);
      }
    );
    return authListener;
  },
};
